import React, { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'
import { ConfirmationModal, Table } from 'components/Ui'
import { deleteApiRecord, updateApiStatus } from 'services/CompanyService'
import { Success } from 'utils/alerts'
import useAuth from 'utils/hooks/useAuth'
import { status, Title, verifyStatus } from 'components/helper'
import dayjs from 'dayjs'
injectReducer('company', reducer)

export default function Company() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const { data, tableData, loading } = useSelector(
        (state) => state.company.data,
    )

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.checkBox,
    ])

    const fetchData = () => dispatch(getData({ ...tableData }))

    const handleDelete = (id) => {
        deleteApiRecord(id)
            .then(() => {
                Success('Deleted Successfully!')
                fetchData()
            })
            .catch((error) => error)
    }

    const handleStatus = (id, data) => {
        delete data.image
        updateApiStatus(id, data)
            .then(() => {
                Success('Status Updated Successfully!')
                fetchData()
            })
            .catch((error) => error)
    }

    const columns = useMemo(
        () => [
            {
                field: 'name',
                header: 'Name',
                className: 'min-w-[150px]',
                renderCell: (row) => (
                    <Title title={row?.name} image={row?.company_logo} />
                ),
            },
            {
                field: 'email',
                header: 'Email',
                className: 'min-w-[150px]',
            },
            {
                field: 'date_of_establishment',
                header: 'Date of Establishment',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.date_of_establishment
                        ? dayjs(row?.date_of_establishment).format(
                              'DD-MMM-YYYY',
                          )
                        : '',
            },
            {
                field: 'is_active',
                header: 'Status',
                renderCell: (row) => {
                    return checkPermission('company', 'edit') ? (
                        <ConfirmationModal
                            onConfirm={() => {
                                handleStatus(row.id, {
                                    is_active: !row.is_active,
                                })
                            }}
                        >
                            {status(row.is_active)}
                        </ConfirmationModal>
                    ) : (
                        status(row.is_active)
                    )
                },
            },
            {
                field: 'is_verified',
                header: 'Verified',
                renderCell: (row) => {
                    return checkPermission('company', 'edit') &&
                        !row.is_verified ? (
                        <ConfirmationModal
                            onConfirm={() => {
                                handleStatus(row.id, {
                                    is_verified: !row.is_verified,
                                })
                            }}
                        >
                            {verifyStatus(row.is_verified)}
                        </ConfirmationModal>
                    ) : (
                        verifyStatus(row.is_verified)
                    )
                },
            },
            {
                header: 'Action',
                field: 'action',
                className: 'px-4',
                renderCell: (row) => {
                    return (
                        <div className="flex items-center gap-3">
                            {checkPermission('company', 'edit') ? (
                                <Tooltip title={'Edit'}>
                                    <Link
                                        to={`/company-management/company/${row.id}/edit`}
                                        className="hover:text-primary"
                                    >
                                        <HiOutlinePencil size={20} />
                                    </Link>
                                </Tooltip>
                            ) : null}
                            {checkPermission('company', 'delete') ? (
                                <Tooltip title={'Delete'}>
                                    <ConfirmationModal
                                        message={
                                            'Are you sure you want to delete this record!'
                                        }
                                        onConfirm={() => {
                                            handleDelete(row.id)
                                        }}
                                    >
                                        <span className="hover:text-danger">
                                            <HiOutlineTrash size={20} />
                                        </span>
                                    </ConfirmationModal>
                                </Tooltip>
                            ) : null}
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [
            tableData.sort,
            tableData.query,
            tableData.pageIndex,
            tableData.pageSize,
            tableData.selectedTab,
            tableData.checkBox,
        ],
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">
                    Company
                </h2>
            </div>
            <Table
                loading={loading}
                datas={data}
                columns={columns}
                add_new={
                    checkPermission('company', 'create')
                        ? '/company-management/company/create'
                        : ''
                }
                pagingData={tableData}
                onChange={(e) => dispatch(setTableData(e))}
            />
        </>
    )
}
